<template>
    <LayoutModal v-model="model">
        <div
            v-click-outside="close"
            class="max-h-screen w-full max-w-xl border ct-border-1 ct-bg-1--alt rounded-xl flex flex-col"
        >
            <div class="relative flex-grow overflow-y-auto p-6 space-y-6">
                <div class="space-y-2">
                    <p class="flex-grow text-2xl font-medium ct-text-1">
                        {{ $t("r_player.certificate.share_award") }}
                    </p>

                    <p class="ct-text-3 text-sm">
                        {{ $t("r_player.certificate.congratulations") }}
                    </p>
                    <ButtonCloseSmXs class="absolute top-4 right-4" @click="close" />
                </div>

                <FormSwitch :options="types" v-model="type" />
                <div
                    class="relative w-full rounded-xl"
                    :class="isBadgeType ? '' : 'ct-bg-3 p-5 '"
                    :style="{ height: iframeContainerHeight }"
                >
                    <CardPreloader class="absolute inset-0" v-if="!certificateLoaded" />
                    <iframe
                        id="cert-preview"
                        @load="adjustIframeScale"
                        :src="iframeSrc"
                        class="w-full"
                        :class="isBadgeType ? 'h-[460px]' : 'h-[420px]'"
                    />
                </div>
                <div class="space-y-3">
                    <div class="grid grid-cols-2 gap-3">
                        <ButtonSecondaryMd @click="copyThis">
                            <LinkChainIcon />
                            <span>{{ $t("r_player.certificate.copy_link") }}</span>
                        </ButtonSecondaryMd>
                        <ButtonPrimaryMd class="w-full" @click="goToPage">
                            <LinkShareIcon />
                            <span>{{
                                isBadgeType
                                    ? $t("r_player.certificate.badge_link")
                                    : $t("r_player.certificate.certificate_link")
                            }}</span>
                        </ButtonPrimaryMd>
                    </div>
                </div>
            </div>
        </div>
    </LayoutModal>
</template>

<script>
import { mapActions } from "vuex";
import { copy } from "@/shared/utils/string";
import { LayoutModal } from "@easy/ui/layouts";
import { FormSwitch } from "@easy/ui/forms";
import { ButtonCloseSmXs, ButtonPrimaryMd, ButtonSecondaryMd } from "@easy/ui/buttons";
import { LinkChainIcon, LinkShareIcon } from "@easy/ui/icons";
import { CardPreloader } from "@easy/ui/cards";
import { RENDER_ROUTE_NAMES } from "@/views/render/render.routes";
import { waitForMe } from "@/shared/utils/helpers";

export default {
    components: {
        ButtonCloseSmXs,
        LayoutModal,
        FormSwitch,
        ButtonPrimaryMd,
        ButtonSecondaryMd,
        LinkChainIcon,
        LinkShareIcon,
        // DownloadTrayIcon,
        CardPreloader,
    },
    props: {
        modelValue: Boolean,
        playerUuid: String,
        certificateUuid: String,
    },
    emits: ["update:modelValue"],
    data() {
        return {
            iframeContainerHeight: "460px",
            certificateLoaded: false,
            type: "certificate",
        };
    },
    watch: {
        isBadgeType() {
            this.certificateLoaded = false;
        },
    },
    computed: {
        model: {
            get() {
                return this.modelValue;
            },
            set(value) {
                this.$emit("update:modelValue", value);
            },
        },
        isBadgeType() {
            return this.type === "badge";
        },
        types() {
            return [
                { label: this.$t("r_player.certificate.certificate"), value: "certificate" },
                { label: this.$t("r_player.certificate.badge"), value: "badge" },
            ];
        },
        iframeSrc() {
            return this.isBadgeType
                ? `/players/${this.playerUuid}/badges/${this.certificateUuid}?preview=true`
                : `/players/${this.playerUuid}/certificates/${this.certificateUuid}?preview=true`;
        },
    },
    created() {
        if (!localStorage.getItem(this.onboardingHideKey)) {
            this.showOnboarding = true;
        }
    },
    methods: {
        ...mapActions("alert", ["showSuccessMessage"]),
        close() {
            this.model = false;
        },
        goToPage() {
            this.$router.push({
                name: this.isBadgeType
                    ? RENDER_ROUTE_NAMES.playerBadge
                    : RENDER_ROUTE_NAMES.playerCertificate,
                params: { projectUuid: this.playerUuid, certificateUuid: this.certificateUuid },
            });
        },
        async adjustIframeScale() {
            await this.$nextTick();
            await waitForMe(100);

            const iframe = document.getElementById("cert-preview");
            const iframeHeight = iframe.contentWindow.document.body.scrollHeight;

            if (this.isBadgeType) {
                this.iframeContainerHeight = `${iframeHeight}px`;
                iframe.style.transform = "scale(1)";
                iframe.style.transformOrigin = "";
                iframe.style.height = "100%";
            } else {
                this.iframeContainerHeight = "460px";
                const viewportHeight = 420;
                const scaleHeight = viewportHeight / iframeHeight;

                const scale = scaleHeight > 1 ? 1 : scaleHeight;
                iframe.style.transform = `scale(${scale})`;
                iframe.style.transformOrigin = "top";
                iframe.style.height = `${iframeHeight}px`;
            }

            this.certificateLoaded = true;
        },
        copyThis() {
            copy(
                `${window.location.origin}/players/${this.playerUuid}/certificates/${this.certificateUuid}`
            );
            this.showSuccessMessage({
                title: this.$t("r_shared.copied"),
            });
        },
    },
};
</script>
